import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { PdfPageExtractorModal } from '@/components/pdf-extraction';
import { DownloadableFile, DownloadProgressItem } from '@/hooks/useDownload';
import { blobsApiClient } from '@/services/api/blobService';
import { blobSinkActionService } from '@/services/api/blobSinkActionService';
import {
  useDestroyCollectionMutation,
} from '@/services/api/collectionService';
import { useRegenerateCoverageMutation } from '@/services/api/coverageService';
import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';
import { CompanionMember } from '@/services/types/companions-types';

import { UploadProgress as UploadProgressType } from '../../matter-details/documents/types';
import { useDocumentTree } from '../context/DocumentTreeContext';
import { useBulkFileOperations } from '../hooks/useBulkFileOperations';
import { ActionDialog } from './ActionDialog';
import { ArtifactStatsDialog } from './ArtifactStatsDialog';
import { BulkActionBar } from './BulkActionBar';
import { BulkApproveSyncDialog } from './BulkApproveSyncDialog';
import { BulkResortDialog } from './BulkResortDialog';
import { DocumentDialogs } from './DocumentDialogs';
import { DownloadProgress } from './DownloadProgress';
import { OrganizeFilesModal } from './OrganizeFilesModal';
import { SearchBar } from './SearchBar';
import { UploadProgress } from './UploadProgress';

interface ActionBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  blobs: BlobType[] | undefined;
  rootNodeId: string | undefined;
  refetch: () => void;
  showUploadDialog: (targetCollectionId: string) => void;
  collectionTree: CollectionNode | undefined;
  // Upload progress props
  uploadProgress: UploadProgressType[];
  uploadError: string | null;
  dismissUploadError: () => void;
  clearUploads: () => void;
  // Download progress props
  downloadProgress: DownloadProgressItem[];
  downloadError: string | null;
  isGeneratingZip: boolean;
  dismissDownloadError: () => void;
  clearCompletedDownloads: () => void;
  // Upload dialog props
  uploadDialogOpen: boolean;
  uploadDialogTargetId: string | null;
  closeUploadDialog: () => void;
  handleUploadToCollection: (
    files: File[],
    collectionId: string,
  ) => Promise<string[] | void>;
  // Bulk download handler
  downloadFiles?: (files: DownloadableFile[]) => void;
  // Companions
  companions?: CompanionMember[];
  // Matter context for pinning
  matterName?: string;
}

export const ActionBar = ({
  searchQuery,
  setSearchQuery,
  blobs,
  rootNodeId,
  refetch,
  showUploadDialog,
  collectionTree,
  uploadProgress,
  uploadError,
  dismissUploadError,
  clearUploads,
  downloadProgress,
  downloadError,
  isGeneratingZip,
  dismissDownloadError,
  clearCompletedDownloads,
  uploadDialogOpen,
  uploadDialogTargetId,
  closeUploadDialog,
  handleUploadToCollection,
  downloadFiles,
  companions,
  matterName,
}: ActionBarProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRecheckingSync, setIsRecheckingSync] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [organizeFilesModalOpen, setOrganizeFilesModalOpen] = useState(false);
  const [transferToCompanionsDialogOpen, setTransferToCompanionsDialogOpen] =
    useState(false);
  const [bulkApproveSyncDialogOpen, setBulkApproveSyncDialogOpen] =
    useState(false);
  const [extractPagesModalOpen, setExtractPagesModalOpen] = useState(false);
  const [extractPagesBlob, setExtractPagesBlob] = useState<BlobType | null>(
    null,
  );
  const dispatch = useDispatch();

  // Coverage mutation hook
  const [regenerateCoverage] = useRegenerateCoverageMutation();

  // Blob sink actions recheck mutation hook
  const [recheckMatterSinkActions] =
    blobSinkActionService.useBlobSinkActionsRecheckMatterMutation();

  // Access document tree context
  const {
    selectedFileIds,
    clearSelection,
    activeNodeId,
    setActiveNodeId,
    nodeMaps,
    selectedFolderIds,
    clearFolderSelection,
  } = useDocumentTree();

  const [bulkDeleteFoldersDialogOpen, setBulkDeleteFoldersDialogOpen] =
    useState(false);
  const [isFolderDeleteProcessing, setIsFolderDeleteProcessing] =
    useState(false);
  const [destroyCollection] = useDestroyCollectionMutation();

  const handleBulkDeleteFolders = useCallback(() => {
    if (selectedFolderIds.size === 0) return;
    setBulkDeleteFoldersDialogOpen(true);
  }, [selectedFolderIds]);

  const executeBulkDeleteFolders = useCallback(async () => {
    if (!collectionTree?.matter_id || selectedFolderIds.size === 0) return;

    setIsFolderDeleteProcessing(true);
    try {
      await Promise.all(
        Array.from(selectedFolderIds).map((collectionId) =>
          destroyCollection({
            matterId: collectionTree.matter_id,
            collectionId,
          }).unwrap(),
        ),
      );

      setBulkDeleteFoldersDialogOpen(false);
      clearFolderSelection();
      setIsFolderDeleteProcessing(false);
      refetch();
    } catch {
      setIsFolderDeleteProcessing(false);
      toast.error('Failed to delete some folders. Please try again.');
    }
  }, [
    collectionTree?.matter_id,
    selectedFolderIds,
    destroyCollection,
    clearFolderSelection,
    refetch,
  ]);

  // Use bulk file operations hook
  const {
    openBulkMoveDialog,
    openBulkDeleteDialog,
    openBulkRegenerateDialog,
    openBulkResortDialog,
    isProcessing,
    isResortLoading,
    bulkMoveDialogOpen,
    bulkDeleteDialogOpen,
    bulkRegenerateDialogOpen,
    bulkResortDialogOpen,
    bulkOperationError,
    bulkMoveTargetCollectionId,
    closeBulkMoveDialog,
    closeBulkDeleteDialog,
    closeBulkRegenerateDialog,
    closeBulkResortDialog,
    executeBulkMove,
    executeBulkDelete,
    executeBulkRegenerate,
    executeBulkResort,
    setBulkMoveTargetCollectionId,
  } = useBulkFileOperations({
    selectedFileIds,
    matterId: collectionTree?.matter_id,
    clearSelection,
    refetch,
    setActiveNodeId,
    selectedBlobs: blobs,
  });

  // Resolve the collection ID for a given node ID (handles both collection and blob IDs)
  const resolveCollectionId = useCallback(
    (nodeId: string | undefined): string => {
      if (!nodeId) {
        return rootNodeId as string;
      }
      // If it's a collection, use it directly
      if (nodeMaps.idToNodeMap.has(nodeId)) {
        return nodeId;
      }
      // Otherwise it's a blob - find the blob and use its collection_id
      const blob = blobs?.find((b) => b.id === nodeId);
      return blob?.collection_id || (rootNodeId as string);
    },
    [nodeMaps, blobs, rootNodeId],
  );

  // Handle upload button click
  const handleUploadClick = useCallback(() => {
    showUploadDialog(resolveCollectionId(activeNodeId));
  }, [activeNodeId, showUploadDialog, resolveCollectionId]);

  // Bulk operations handlers
  const handleBulkMove = useCallback(() => {
    openBulkMoveDialog(resolveCollectionId(activeNodeId));
  }, [activeNodeId, openBulkMoveDialog, resolveCollectionId]);

  const handleBulkDelete = useCallback(() => {
    openBulkDeleteDialog();
  }, [openBulkDeleteDialog]);

  const handleBulkDownload = useCallback(() => {
    if (!downloadFiles || selectedFileIds.size === 0) return;

    // Filter blobs to get the selected files
    const blobsToDownload =
      blobs?.filter((blob) => selectedFileIds.has(blob.id)) || [];

    if (blobsToDownload.length === 0) return;

    // Convert blobs to DownloadableFile format and use the unified hook
    const filesToDownload: DownloadableFile[] = blobsToDownload.map((blob) => ({
      downloadUrl: blob.download_url,
      name: blob.name,
      fileExtension: blob.file_extension,
    }));

    downloadFiles(filesToDownload);
  }, [selectedFileIds, blobs, downloadFiles]);

  const handleBulkRegenerate = useCallback(() => {
    openBulkRegenerateDialog();
  }, [openBulkRegenerateDialog]);

  // Handle bulk resort - open dialog to show rules first
  const handleBulkResort = useCallback(() => {
    openBulkResortDialog();
  }, [openBulkResortDialog]);

  const handleBulkExtractCoverage = useCallback(async () => {
    if (!collectionTree?.matter_id || selectedFileIds.size === 0) return;

    try {
      // Get artifact IDs from selected blobs
      const selectedBlobs =
        blobs?.filter((blob) => selectedFileIds.has(blob.id)) || [];
      const artifactIds = selectedBlobs.map(
        (blob) => blob.artifact_id as string,
      );

      if (artifactIds.length === 0) return;

      await regenerateCoverage({
        matterId: collectionTree.matter_id,
        artifactIds,
      }).unwrap();

      toast.success(
        `Coverage extraction started for ${artifactIds.length} file(s)`,
      );
      clearSelection();
    } catch (error) {
      console.error('Failed to extract coverage:', error);
      toast.error('Failed to start coverage extraction');
    }
  }, [
    collectionTree?.matter_id,
    selectedFileIds,
    blobs,
    regenerateCoverage,
    clearSelection,
  ]);

  // Handle bulk approve doc sync - opens confirmation dialog
  const handleBulkApproveSync = useCallback(() => {
    setBulkApproveSyncDialogOpen(true);
  }, []);

  // Handle extract pages - open modal with selected PDF
  const handleExtractPages = useCallback(() => {
    if (selectedFileIds.size !== 1) return;

    const selectedBlob = blobs?.find((blob) => selectedFileIds.has(blob.id));
    if (!selectedBlob || selectedBlob.file_mime_type !== 'application/pdf') {
      return;
    }

    setExtractPagesBlob(selectedBlob);
    setExtractPagesModalOpen(true);
  }, [selectedFileIds, blobs]);

  // Handle extract pages complete
  const handleExtractPagesComplete = useCallback(() => {
    setExtractPagesModalOpen(false);
    setExtractPagesBlob(null);
    clearSelection();
    refetch();
  }, [clearSelection, refetch]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const matterId = collectionTree?.matter_id;

      if (matterId && blobs && activeNodeId) {
        // Look for a blob matching the active node ID
        const activeBlob = blobs.find((blob) => blob.id === activeNodeId);

        if (activeBlob) {
          // Invalidate BlobDetail tag for the active blob
          dispatch(
            blobsApiClient.util.invalidateTags([
              { type: 'BlobDetail', id: `${matterId}-${activeBlob.id}` },
            ]),
          );
        }
      }

      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch, dispatch, collectionTree, activeNodeId, blobs]);

  // Handle recheck sync - triggers recheck of blob sink actions for the whole matter
  const handleRecheckSync = useCallback(async () => {
    const matterId = collectionTree?.matter_id;
    if (!matterId) return;

    setIsRecheckingSync(true);
    try {
      const result = await recheckMatterSinkActions({ matterId }).unwrap();
      toast.success(
        `Sync check complete: ${result.actions_created} new actions for ${result.blobs_checked} files`,
      );

      // Refresh the blobs list using the same logic as the refresh button
      if (blobs && activeNodeId) {
        const activeBlob = blobs.find((blob) => blob.id === activeNodeId);
        if (activeBlob) {
          dispatch(
            blobsApiClient.util.invalidateTags([
              { type: 'BlobDetail', id: `${matterId}-${activeBlob.id}` },
            ]),
          );
        }
      }

      await refetch();
    } catch (error) {
      console.error('Failed to recheck sync:', error);
      toast.error('Failed to recheck document sync');
    } finally {
      setIsRecheckingSync(false);
    }
  }, [
    collectionTree?.matter_id,
    recheckMatterSinkActions,
    blobs,
    activeNodeId,
    dispatch,
    refetch,
  ]);

  // Check if any blobs have sink actions requiring human approval
  const hasHumanApprovalSinkActions = useMemo(
    () =>
      blobs?.some((blob) =>
        blob.sink_actions?.some(
          (action) => action.approval_mode === 'HUMAN',
        ),
      ) ?? false,
    [blobs],
  );

  // Compute flagged audit count
  const flaggedAuditCount = useMemo(
    () =>
      blobs?.filter(
        (blob) => blob.artifact_audit?.status === 'FLAGGED_PENDING_REVIEW',
      ).length ?? 0,
    [blobs],
  );

  const handleStatsClick = useCallback(() => {
    setStatsDialogOpen(true);
  }, []);

  // Handle AI-powered file organization - opens the modal
  const handleOrganizeFiles = useCallback(() => {
    setOrganizeFilesModalOpen(true);
  }, []);

  return (
    <div className="flex flex-col border-b text-xs">
      <div className="flex flex-row items-center bg-background ">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          onUploadClick={handleUploadClick}
          onRefreshClick={handleRefresh}
          onRecheckSyncClick={handleRecheckSync}
          onStatsClick={handleStatsClick}
          onOrganizeFilesClick={handleOrganizeFiles}
          isRefreshing={isRefreshing}
          isRecheckingSync={isRecheckingSync}
          flaggedAuditCount={flaggedAuditCount}
        />
        <BulkActionBar
          onBulkMove={handleBulkMove}
          onBulkDelete={handleBulkDelete}
          onBulkDownload={handleBulkDownload}
          onBulkRegenerate={handleBulkRegenerate}
          onBulkExtractCoverage={handleBulkExtractCoverage}
          onBulkResort={handleBulkResort}
          onBulkApproveSync={
            hasHumanApprovalSinkActions ? handleBulkApproveSync : undefined
          }
          onExtractPages={handleExtractPages}
          onTransferToCompanions={() => setTransferToCompanionsDialogOpen(true)}
          hasCompanions={(companions?.length ?? 0) > 1}
          blobs={blobs}
          matterId={collectionTree?.matter_id}
          matterName={matterName}
          isResortLoading={isResortLoading}
          onBulkDeleteFolders={handleBulkDeleteFolders}
        />
      </div>

      {/* Upload Progress and Errors */}
      <UploadProgress
        progress={uploadProgress}
        error={uploadError}
        onDismissError={dismissUploadError}
        onClearCompleted={clearUploads}
      />

      {/* Download Progress and Errors */}
      <DownloadProgress
        progress={downloadProgress}
        error={downloadError}
        isGeneratingZip={isGeneratingZip}
        onDismissError={dismissDownloadError}
        onClearCompleted={clearCompletedDownloads}
      />

      {/* Document Dialogs Component */}
      <DocumentDialogs
        collectionTree={collectionTree}
        // Upload dialog props
        uploadDialogOpen={uploadDialogOpen}
        uploadDialogTargetId={uploadDialogTargetId}
        closeUploadDialog={closeUploadDialog}
        handleUploadToCollection={handleUploadToCollection}
        companions={companions}
        // Bulk move dialog props
        bulkMoveDialogOpen={bulkMoveDialogOpen}
        bulkMoveTargetCollectionId={bulkMoveTargetCollectionId}
        closeBulkMoveDialog={closeBulkMoveDialog}
        executeBulkMove={executeBulkMove}
        setBulkMoveTargetCollectionId={setBulkMoveTargetCollectionId}
        bulkOperationError={bulkOperationError}
        isProcessing={isProcessing}
        selectedFileCount={selectedFileIds.size}
        // Bulk delete dialog props
        bulkDeleteDialogOpen={bulkDeleteDialogOpen}
        closeBulkDeleteDialog={closeBulkDeleteDialog}
        executeBulkDelete={executeBulkDelete}
        // Bulk regenerate dialog props
        bulkRegenerateDialogOpen={bulkRegenerateDialogOpen}
        closeBulkRegenerateDialog={closeBulkRegenerateDialog}
        executeBulkRegenerate={executeBulkRegenerate}
        // Transfer to companions dialog props
        transferToCompanionsDialogOpen={transferToCompanionsDialogOpen}
        closeTransferToCompanionsDialog={() =>
          setTransferToCompanionsDialogOpen(false)
        }
        selectedBlobIds={Array.from(selectedFileIds)}
        onTransferSuccess={() => {
          clearSelection();
          refetch();
        }}
      />

      {/* Bulk Delete Folders Dialog */}
      <ActionDialog
        isOpen={bulkDeleteFoldersDialogOpen}
        onClose={() => setBulkDeleteFoldersDialogOpen(false)}
        title="Delete Folders"
        description={`Are you sure you want to delete ${selectedFolderIds.size} selected folder(s)? This action cannot be undone and will delete all files within these folders.`}
        confirmButtonText="Delete"
        onConfirm={executeBulkDeleteFolders}
        isConfirmDisabled={isFolderDeleteProcessing}
        isDangerousAction
      />

      {/* Artifact Stats Dialog */}
      {collectionTree?.matter_id && (
        <ArtifactStatsDialog
          isOpen={statsDialogOpen}
          onClose={() => setStatsDialogOpen(false)}
          matterId={collectionTree.matter_id}
        />
      )}

      {/* PDF Page Extraction Modal */}
      {collectionTree?.matter_id && extractPagesBlob && (
        <PdfPageExtractorModal
          isOpen={extractPagesModalOpen}
          onClose={() => {
            setExtractPagesModalOpen(false);
            setExtractPagesBlob(null);
          }}
          matterId={collectionTree.matter_id}
          sourceBlobId={extractPagesBlob.id}
          sourceUrl={extractPagesBlob.download_url}
          sourceName={extractPagesBlob.name}
          defaultCollectionId={extractPagesBlob.collection_id}
          onExtractComplete={handleExtractPagesComplete}
          context="documents"
        />
      )}

      {/* Organize Files Modal */}
      {collectionTree?.matter_id && (
        <OrganizeFilesModal
          isOpen={organizeFilesModalOpen}
          onClose={() => {
            setOrganizeFilesModalOpen(false);
            refetch(); // Refresh files after organizing
          }}
          matterId={collectionTree.matter_id}
        />
      )}

      {/* Bulk Re-Sort Dialog */}
      {collectionTree?.matter_id && (
        <BulkResortDialog
          isOpen={bulkResortDialogOpen}
          onClose={closeBulkResortDialog}
          onConfirm={executeBulkResort}
          matterId={collectionTree.matter_id}
          selectedCount={selectedFileIds.size}
          isLoading={isResortLoading}
        />
      )}

      {/* Bulk Approve Sync Dialog */}
      {collectionTree?.matter_id && (
        <BulkApproveSyncDialog
          isOpen={bulkApproveSyncDialogOpen}
          onClose={() => setBulkApproveSyncDialogOpen(false)}
          matterId={collectionTree.matter_id}
          selectedBlobs={
            blobs?.filter((blob) => selectedFileIds.has(blob.id)) || []
          }
          onSuccess={() => {
            clearSelection();
            refetch();
          }}
        />
      )}

      {/* Bulk Re-Sort Progress now shown in WorkflowIndicator ("Finch is Finching" section) */}
    </div>
  );
};
